import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sidebar, Menu, MenuItem, SubMenu, sidebarClasses } from 'react-pro-sidebar';
import { ReactComponent as LogoText } from '../../images/logo.svg';
import { ReactComponent as LogoBrand } from '../../images/logo-text.svg';
import {
  RiDashboardFill,
  RiFileSearchFill,
  RiUser3Fill,
  RiSettings3Fill,
  RiCloseLine,
} from "react-icons/ri";
import { GrDocumentUser } from "react-icons/gr";
import { ImProfile } from "react-icons/im";
import { PiArchiveDuotone } from "react-icons/pi";
import { FaUserShield } from "react-icons/fa";
import { useMobileNav } from '../../contexts/MobileNavContext';
import { hasPrivilegedRole, isActingAsAdmin } from '../../utils/authUtils';
// One table for the console's links, the router's paths and the layout's
// titles — see the module comment for what the duplicated copies cost.
import { ADMIN_NAV } from '../../pages/admin/adminNav';
import css from './SideMenu.module.scss';

const SideMenu: React.FC = () => {
  // Open by default -- the console nav is the admin's primary nav, not
  // something to discover behind a disclosure. Controlled rather than
  // `defaultOpen` only so `aria-expanded` below can tell the truth.
  const [adminOpen, setAdminOpen] = useState(true);
  // Both gates are FUNCTIONS and must be called — a bare reference is always
  // truthy and opens the gate for everyone. See src/utils/authUtils.ts.

  // Holds an admin role AND is not impersonating: this session IS the console,
  // so it gets the console nav instead of the app nav.
  const isAdminSession = isActingAsAdmin();
  // Permissive on purpose: an impersonating admin keeps a way back to /admin —
  // the only surface that can stop the impersonation — alongside the
  // impersonated user's menu. Narrowing this to isActingAsAdmin() strands them.
  const canReachAdmin = hasPrivilegedRole();
  const { isNavOpen, closeNav } = useMobileNav();
  const location = useLocation();

  // WHERE the reader is, not only who they are. `AdminShell` renders this same
  // sidebar, so on a console route `isAdminSession` alone decided the nav for a
  // page it does not describe: an impersonating admin who clicked the escape
  // hatch landed on /admin/overview with the IMPERSONATED USER'S app links down
  // the side and a lone "Admin" item pointing at the page they were already on.
  // Users, Activity, System and Integrations were unreachable, so the way back
  // to the console was also a dead end — the admin could neither administer nor
  // leave without signing out.
  const onConsole = location.pathname.startsWith('/admin');
  // The console's own nav whenever the console is on screen AND the reader is
  // entitled to it. `canReachAdmin` on the right-hand side is not redundant
  // with `AdminRoute`: the guard redirects a plain user away from /admin, but
  // it redirects during a render this sidebar has already painted, and a bare
  // `onConsole` would spend that frame swapping their own menu for a console
  // one. For a non-impersonating admin the left half is true everywhere and
  // nothing changes; for a plain user neither half is ever true.
  //
  // THIS GOVERNS THE ADMIN GROUP ONLY. The app links below are gated on
  // `isAdminSession`, and the two are not opposites: mid-impersonation BOTH
  // menus belong on screen. Using this to hide the app links cost the admin the
  // route INTO the account they had just opened — `handleImpersonate` ends in
  // `window.location.reload()`, so an impersonation begins on /admin/users, and
  // the sidebar there answered with five console links and no Dashboard.
  const showConsoleNav = isAdminSession || (onConsole && canReachAdmin);

  // Navigating from a menu item should dismiss the mobile drawer; no-op on
  // desktop where the drawer never opens.
  useEffect(() => {
    closeNav();
  }, [location.pathname, closeNav]);

  return (
    <>
    <div
      className={`${css.overlay} ${isNavOpen ? css.overlayOpen : ''}`}
      onClick={closeNav}
      aria-hidden="true"
    />
    <div className={`${css["side-menu"]} ${isNavOpen ? css.open : ''}`}>
      <div className={css.drawerHeader}>
        <LogoBrand className={css.drawerLogo} />
        <button className={css.drawerClose} onClick={closeNav} aria-label="Close menu">
          <RiCloseLine />
        </button>
      </div>
      <Sidebar
        className={css['side-menu-in']}
        rootStyles={{
          [`.${sidebarClasses.container}`]: {
            backgroundColor: '#f0f0f0',
          },
        }}
      >
        <Menu
          menuItemStyles={{
            button: {
              backgroundColor: undefined,
              color: "#231f46",
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(32, 117, 81, 0.1)',
                color: "#207551",
                paddingLeft: '24px',
              },
              '&:active': {
                color: "#185d40",
                backgroundColor: 'rgba(32, 117, 81, 0.15)',
              },
            },
            icon: {
              backgroundColor: undefined,
              color: "#207551",
              transition: 'all 0.2s ease',
              '&:hover': {
                color: "#185d40",
              },
              '&:active': { color: "#185d40" },
            },
          }}
        >
          {/*
            `isAdminSession`, NOT `!showConsoleNav`. An impersonating admin is
            driving someone's account and needs its pages from wherever they
            are, the console included — the two menus stack rather than take
            turns. Only an admin who is NOT impersonating has no app of their
            own to link to.
          */}
          {!isAdminSession && (
            <>
              <MenuItem
                icon={<RiDashboardFill />}
                component={<Link to="/" />}
                className={css["menu-item"]}
              >
                Dashboard
              </MenuItem>
              <MenuItem
                icon={<RiFileSearchFill />}
                component={<Link to="/searches" />}
                className={css["menu-item"]}
              >
                Searches
              </MenuItem>
              <MenuItem
                icon={<ImProfile />}
                component={<Link to="/opportunities" />}
                className={css["menu-item"]}
              >
                Opportunities
              </MenuItem>
              <MenuItem
                icon={<GrDocumentUser />}
                component={<Link to="/applications" />}
                className={css["menu-item"]}
              >
                Applications
              </MenuItem>
              <MenuItem
                icon={<PiArchiveDuotone />}
                component={<Link to="/archive" />}
                className={css["menu-item"]}
              >
                Archive
              </MenuItem>
              <MenuItem
                icon={<RiUser3Fill />}
                component={<Link to="/profile" />}
                className={css["menu-item"]}
              >
                Profile
              </MenuItem>
              <MenuItem
                icon={<RiSettings3Fill />}
                component={<Link to="/settings" />}
                className={css["menu-item"]}
              >
                Settings
              </MenuItem>
            </>
          )}
          {canReachAdmin && (showConsoleNav ? (
            <SubMenu
              label="Admin"
              icon={<FaUserShield />}
              open={adminOpen}
              onOpenChange={setAdminOpen}
              // The library renders the toggle as an <a> with no href, so
              // without these it is announced as static text and a screen
              // reader user gets no signal that it is a control or that the
              // five children below it are already showing. It was a real
              // <a href="/admin"> before this change, so silence here is a
              // regression rather than a pre-existing gap.
              role="button"
              aria-expanded={adminOpen}
              className={`${css["menu-item"]} ${css.adminGroup}`}
            >
              {ADMIN_NAV.map(({ label, path, icon }) => (
                <MenuItem
                  key={path}
                  icon={icon}
                  component={<Link to={path} />}
                  // startsWith, not equality: nested console routes such as
                  // /admin/users/:id must keep their parent link lit.
                  active={location.pathname.startsWith(path)}
                  className={css.adminGroupItem}
                >
                  {label}
                </MenuItem>
              ))}
            </SubMenu>
          ) : (
            // Mid-impersonation, ON THE TARGET USER'S OWN APP. The stored
            // accessToken belongs to them and the admin is looking at their
            // pages, so the only thing they need from the console is the way
            // in — the group collapses to the single escape hatch rather than
            // dropping four administer-other-people links into someone else's
            // session. Once that link is followed, `onConsole` above takes over
            // and the full console nav returns.
            <MenuItem
              icon={<FaUserShield />}
              component={<Link to="/admin" />}
              className={css["menu-item"]}
            >
              Admin
            </MenuItem>
          ))}
        </Menu>
      </Sidebar>
      <div className={css.footer}>
        <LogoText className={css["footer-image"]} />
      </div>
    </div>
    </>
  );
};

export default SideMenu;